import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import React from 'react';
import Layout from '../../layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import InfluencersContent from '@/components/InfluencersContent';
import CustomHead from '@/components/Common/CustomHead';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page2JsonLd } from '@/lib/jsonld';
import { fetchHairstyleBloggerLabelsData, fetchHairstyleBloggerPageSearchData } from '@/lib/service';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl, query } = context;
  const { type = '0', label, search_word } = query;
  const getTempTitle = () => {
    if (`${type}` === '0') {
      if (label !== undefined) {
        return `Try on a Variety of ${label} Hairstyles`;
      }
      return `Variety of Hairstyles to Try On | Hairpaca AI hairstyle generator`;
    } else {
      return `Top Hairstyle Influencers & Latest Trends | Hairpaca`;
    }
  };
  const getTempDesc = () => {
    if (`${type}` === '0') {
      return `Browse and try on a variety of hairstyles with Hairpaca. Discover your perfect look and stay updated with the latest hairstyle trends.`;
    } else {
      return `Explore top hairstyle influencers and their latest styles on Hairpaca. Stay updated with popular trends and find inspiration for your next look.`;
    }
  };
  const localeRes = await serverSideTranslations(locale, ['common']);
  const tdkData = await getTdkApi(resolvedUrl, locale);

  const jsonLd = page2JsonLd(
    {
      title: tdkData?.data?.title ?? getTempTitle(),
      desc: tdkData?.data?.description ?? getTempDesc(),
      url: canonicalUrl(req.headers.host, resolvedUrl, locale)
    },
    {
      url: canonicalUrl(req.headers.host, '/', locale)
    }
  );

  // 在服务端获取初始数据
  const params = {
    page_no: 1,
    page_size: 30,
    label: label as string,
    search_word: search_word as string
  };

  // 获取博主标签数据
  const labelsResponse = await fetchHairstyleBloggerLabelsData(locale);
  const bloggerLabels: string[] = labelsResponse?.code === 0 ? labelsResponse.data : [];

  // 获取博主列表数据
  const bloggerResponse = await fetchHairstyleBloggerPageSearchData(params);
  const bloggerData = bloggerResponse?.code === 0 ? bloggerResponse : null;

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkData?.data?.title ?? getTempTitle(),
      description: tdkData?.data?.description ?? getTempDesc(),
      jsonLd,
      // 传递初始数据
      initialBloggerLabels: bloggerLabels || [],
      initialBloggerData: bloggerData || null,
      initialSearchParams: {
        label: (label as string) || null,
        search_word: (search_word as string) || null
      }
    }
  };
};

const InfluencersContentIndex: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <InfluencersContent data={props} />;
};

InfluencersContentIndex.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title ?? '';
  const desc = page?.props?.description ?? '';

  return (
    <Layout
      head={
        <CustomHead>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </CustomHead>
      }
    >
      {page}
    </Layout>
  );
};

export default InfluencersContentIndex;
